import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { TableCell, TableRow, Checkbox, Collapse } from '@mui/material';
import TooltipComponent from '@/components/UI/Tooltip/TooltipComponent';
import useError from '@/hooks/useError';
import { updatePlant } from '@/redux/Campaign/campaign.action';
import { getSelectedPlants } from '@/redux/Campaign/campaign.reducer';
import affectedVehicleServices from '@/services/affectedVehicleService';
import { ObjLimitationPlant, WKZNamePlant } from '@/types';
import ChipsMultiSelector from './ChipsMultiSelector/ChipsMultiSelector';
import PlantChips from './PlantChips/PlantChips';
import { excludeObjectFromValue, filterObject, removeKey } from './utils/type-versions.utils';
import SeriesDropdown from '../SeriesDropdown/SeriesDropdown';
import styles from './SeriesRow.module.scss';

type PlantsRowProps = {
  selection: ObjLimitationPlant;
  checkEnabled: boolean;
  isEditable: boolean;
  index: number;
  options: { plants: WKZNamePlant[] };
  plantsSelected: ObjLimitationPlant[];
  updatePlants: (index: number, plant: any, hasChange?: boolean) => void;
  handleDeletePlants: (wkzNumber: string) => void;
};

const PlantsRow: React.FC<PlantsRowProps> = (props) => {
  const { selection, checkEnabled, isEditable, index, options, plantsSelected,
    updatePlants, handleDeletePlants } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [modelSeries, setModelSeries] = useState({});
  const { handleCriticalError } = useError();
  const [open, setOpen] = useState(false);
  const plantsCodes = plantsSelected?.map((plant) => plant.wkzNumber);
  const filteredPlants = options?.plants?.filter((plant) => !plantsCodes.includes(plant.wkzNumber)).map((plant) => plant.plantName);
  const typeVersions = useMemo(() => ([...new Set(Object.values(selection?.affectedSeries || {}).flat())]), [selection]);
  const { t } = useTranslation();

  useEffect(() => {
    if (selection.wkzNumber) {
      affectedVehicleServices.getSeriesByPlant(selection.wkzNumber)
        .then((affectedSeries) => {
          setModelSeries(affectedSeries?.seriesByPlant?.[selection.wkzNumber]);
        }).catch(handleCriticalError);
    }
  }, [selection.wkzNumber]);

  const toggleRow = () => {
    const newPlant = {
      ...selection,
      checked: !selection.checked,
    };

    updatePlants(index, newPlant);
  };

  const modifyPlant = (update, hasChange = false) => {
    const newPlant = {
      ...selection,
      ...update,
    };

    updatePlants(index, newPlant, hasChange);
  };

  const handleChangeSelectedPlants = (value: string | null) => {
    const plant = options?.plants?.find((curPlant) => curPlant.plantName === value);

    if (!plant) {
      handleDeletePlants(selection.wkzNumber);
    } else if (plant?.wkzNumber) {
      setIsLoading(true);
      affectedVehicleServices.getSeriesByPlant(plant.wkzNumber)
        .then((affectedSeries) => {
          const series = affectedSeries?.seriesByPlant?.[plant.wkzNumber];
          setModelSeries(series);
          modifyPlant({ ...plant, affectedSeries: series, checked: false }, true);
        })
        .catch((err) => {
          handleCriticalError(err);
          setIsLoading(false);
        });
    }
  };

  const handleModelSeriesSelectionChange = (ids) => {
    const affectedSeries = filterObject(ids, modelSeries);
    modifyPlant({ affectedSeries }, true);
  };

  const handleRemoveModelSeries = (id) => {
    const affectedSeries = removeKey(selection?.affectedSeries, id);
    modifyPlant({ affectedSeries }, true);
  };

  const handleRemoveTypeVersion = (removeId) => {
    const affectedSeries = excludeObjectFromValue(removeId, selection?.affectedSeries);
    modifyPlant({ affectedSeries }, true);
  };

  return (
    <>
      <TableRow className={styles.TableBodyRow}>
        {isEditable && (
          <TableCell padding="checkbox">
            <Checkbox
              key={`${selection.wkzNumber}-${Math.random()}`}
              color="primary"
              checked={selection.checked}
              disabled={isLoading}
              onClick={toggleRow}
            />
          </TableCell>
        )}
        <TableCell>
          <TooltipComponent title={t('affectedVehicles.arrowCollapsedModelSerie')}>
            <i
              className={`di ${open ? 'icon-pfeil-chevron-unten' : 'icon-pfeil-chevron-rechts'}`}
              onClick={(e) => {
                e.stopPropagation();
                setOpen(!open);
              }}
            />
          </TooltipComponent>
        </TableCell>
        <TableCell className={styles.Cell} align="left">
          <SeriesDropdown
            checkEnabled={checkEnabled || isLoading}
            handleChange={(event, value) => handleChangeSelectedPlants(value)}
            id={`plants-dropdown-selector[${index}]`}
            fieldPath={['affectedVehicles', `affectedPlants[${index}]`, 'plantName']}
            options={filteredPlants}
            value={selection?.plantName}
            placeHolder={t('affectedVehicles.plants')}
          />
        </TableCell>
        <TableCell className={styles.Cell} align="left">
          <ChipsMultiSelector
            id={`plants-model-series-selector[${index}]`}
            fieldPath={['affectedVehicles', `affectedPlants[${index}]`, 'affectedSeries']}
            checkEnabled={checkEnabled || isLoading}
            formControlClass={styles.FullWidth}
            onSelectionChange={(ev) => handleModelSeriesSelectionChange(ev.target.value)}
            onDelete={(deletedItem) => handleRemoveModelSeries(deletedItem)}
            options={Object.keys(modelSeries || {})}
            value={Object.keys(selection?.affectedSeries || {})}
          />
        </TableCell>
        <TableCell className={styles.Cell} align="left">
          <PlantChips
            checkEnabled={checkEnabled}
            list={typeVersions}
            onChange={handleRemoveTypeVersion}
            fieldPath={['affectedVehicles', `affectedPlants[${index}]`, 'affectedSeries', 'typeVersions']}
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingLeft: 4, paddingTop: 0, paddingRight: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            {Object.keys(selection?.affectedSeries).map((data) => (
              <TableRow key={data}>
                <TableCell style={{ width: '2%' }} />
                <TableCell style={{ width: '5%' }} />
                <TableCell style={{ width: '12%' }} />
                <TableCell style={{ width: '50%' }} align="center">
                  {data}
                </TableCell>
                <TableCell style={{ width: '40%' }} align="center">
                  {selection?.affectedSeries?.[data]?.join(', ')}
                </TableCell>
              </TableRow>
            ))}
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const mapStateToProps = (state) => ({
  plantsSelected: getSelectedPlants(state) || [],
});

const actionCreators = {
  updatePlants: updatePlant,
};

export default connect(mapStateToProps, actionCreators)(PlantsRow);
